import { Injectable } from '@angular/core';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

export type LineType = 'fixedline' | 'mobile' | 'unknown';

export interface DeviceContext {
  countryCode?: '49';      // default '49' if omitted
  defaultNdc?: string;     // e.g. '30' for Berlin (no leading 0)
  lineType?: LineType;
}

export interface NormalizedDE {
  kind: 'short' | 'localWithoutNdc' | 'nationalWithNac' | 'international';
  e164?: string;           // +49...
  national?: string;       // 0NDC + SN or short number
  display: string;         // formatted for UI
}

// Your existing short patterns are fine to keep:
const SHORT_DE_PATTERNS: RegExp[] = [
  /^(110|112|115)$/,
  /^116\d{3}$/,
  /^118\d{2,3}$/
];

function digitsOnly(s: string): string {
  return (s || '').replace(/\D/g, '');
}

function isShortNumberDE(d: string): boolean {
  if (!d || d.length < 3 || d.length > 6) return false;
  return SHORT_DE_PATTERNS.some(rx => rx.test(d));
}

/**
 * Remove the optional trunk "(0)" that users often type after +49 / 0049.
 * +49(0)30...  -> +4930...
 * 0049(0)30... -> 004930...
 */
function stripOptionalTrunkZero(raw: string): string {
  if (!raw) return raw;
  return raw
    .replace(/^\s*\+49\s*\(?0\)?\s*/i, '+49')
    .replace(/^\s*0049\s*\(?0\)?\s*/i, '0049');
}

@Injectable({ providedIn: 'root' })
export class GermanPhoneNormalizerService {
  private phoneUtil = PhoneNumberUtil.getInstance();

  normalize(input: string, ctx: DeviceContext = {}): NormalizedDE {
    const cc = ctx.countryCode || '49';
    const raw = (input || '').trim();

    if (!raw) return { kind: 'localWithoutNdc', display: '' };

    // 1) Short numbers: keep as entered (don’t E.164 format)
    const d0 = digitsOnly(raw);
    if (isShortNumberDE(d0)) {
      return { kind: 'short', national: d0, display: d0 };
    }

    // 2) Pre-clean +49(0) / 0049(0)
    const cleaned = stripOptionalTrunkZero(raw);

    // 3) If it looks like a pure local subscriber number (no 0, no +, no 00),
    //    only build a full national number when we have defaultNdc.
    const digits = digitsOnly(cleaned);
    const looksInternational = cleaned.startsWith('+') || digits.startsWith('00');
    const looksNational = digits.startsWith('0');

    if (!looksInternational && !looksNational) {
      if (ctx.defaultNdc) {
        const ndc = ctx.defaultNdc.replace(/^0+/, '');
        const nationalGuess = `0${ndc}${digits}`;

        // Parse and format using libphonenumber
        const pn = this.phoneUtil.parseAndKeepRawInput(nationalGuess, 'DE');
        const e164Str = this.phoneUtil.format(pn, PhoneNumberFormat.E164);
        const natStr = this.phoneUtil.format(pn, PhoneNumberFormat.NATIONAL);

        return {
          kind: 'localWithoutNdc',
          e164: e164Str,          // should be +49...
          national: nationalGuess,
          display: natStr
        };
      }

      // no context: don’t guess
      return { kind: 'localWithoutNdc', national: digits, display: digits };
    }

    // 4) Otherwise parse as DE (handles +49..., 0049..., 0...)
    const pn = this.phoneUtil.parseAndKeepRawInput(cleaned, 'DE');

    // Optional: you can decide whether to require valid numbers:
    // const valid = this.phoneUtil.isValidNumberForRegion(pn, 'DE');

    const e164Str = this.phoneUtil.format(pn, PhoneNumberFormat.E164);
    const natStr = this.phoneUtil.format(pn, PhoneNumberFormat.NATIONAL);

    // Preserve your output "national" style: 0 + national significant number
    // E164 is +49 + NSN (no trunk 0)
    const nsn = e164Str.replace(/^\+49/, '');
    const national = `0${nsn}`;

    // classify kind
    const kind: NormalizedDE['kind'] =
      cleaned.startsWith('+') || digits.startsWith('00') ? 'international' : 'nationalWithNac';

    return {
      kind,
      e164: e164Str,
      national,
      display: natStr
    };
  }
}
